import { withTopbarProps } from '@jusbrasil/topbar'
import { init as initFingerprint } from '@jusbrasil-web/auth/fingerprint-collector'
import { withApollo } from '@jusbrasil-web/core-apollo'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { internalAuthApi } from '../../api'
import { AuthLayout, EmailSignIn } from '../../components'
import { JUSID_PLATFORM_WEB_AUTH, MODAL_EVENTS, PRODUCTION_ORIGIN } from '../../constants'
import type {
  EntityNoticePageKind,
  EntityNoticeVariant,
} from '../../experiments/entity-notice-fakedoor'
import {
  resolveEntityNoticeParams,
  suppressSignupModalExposure,
} from '../../experiments/entity-notice-fakedoor'
import { resolveSignupModalVariant } from '../../experiments/signup-modal'
import type { SignupModalVariant } from '../../experiments/signup-modal/flag'
import type { SignupModalSegment } from '../../experiments/signup-modal/segment'
import { useSignUpOrSignIn } from '../../hooks/useSignUpOrSignIn'
import { getBasePath } from '../../utils/base-path'
import { withCampaignFallback } from '../../utils/campaign-attribution'
import { getEventDataFromQuery } from '../../utils/event-data'

export interface LoginPageProps {
  basePath?: string
  initialEmail?: string
  appId?: string
  featureCode?: string
  fromComponent?: string
  nextUrl?: string
  csrfToken?: string | null
  signupModalForcedVariant?: SignupModalVariant | null
  signupModalSegment?: SignupModalSegment
  signupModalEntityName?: string | null
  signupModalEntityType?: 'PF' | 'CNPJ' | null
  signupModalExposureEligible?: boolean
  signupModalInExperiment?: boolean
  entityNoticeVariant?: EntityNoticeVariant | null
  entityNoticeEntityId?: string | null
  entityNoticePageKind?: EntityNoticePageKind | null
}

export function LoginPage(incomingProps?: LoginPageProps) {
  const {
    basePath = '',
    initialEmail: initialEmailProp,
    nextUrl: nextUrlProp,
    appId,
    featureCode,
    fromComponent,
  } = incomingProps ?? {}
  const router = useRouter()
  const nextUrl = nextUrlProp || (router.query.next_url as string)
  const scope = getFirstQueryParam(router.query.scope) || undefined

  // Bootstraps the auth fingerprint collector once on page mount so that
  // useLogin.handlePasswordSubmit can call get() synchronously when the
  // user submits the password. init() is idempotent and fail-safe.
  useEffect(() => {
    try {
      initFingerprint()
    } catch {
      // fail-open: never block the login page on fingerprint init.
    }
  }, [])

  useEffect(() => {
    const params: Record<string, string> = {
      app_id: appId ?? JUSID_PLATFORM_WEB_AUTH,
      jusid_platform: JUSID_PLATFORM_WEB_AUTH,
      type: 'LogInModal',
    }
    if (featureCode) params.feature_code = featureCode
    if (fromComponent) params.from_component = fromComponent
    sendMetrics(MODAL_EVENTS.SEEN, null, params)
  }, [appId, featureCode, fromComponent])

  const emailFromQuery = router.query.email as string
  const bigAccountsInviteEmail = router.query.bigAccountsInviteEmail as string
  const initialEmail = initialEmailProp || emailFromQuery || bigAccountsInviteEmail || ''

  const { isLoading, error, handleEmailSubmit } = useSignUpOrSignIn({
    appId,
    basePath,
    featureCode,
    fromComponent,
    nextUrl,
    scope,
    initialEmail,
    allowsOtp: true,
  })

  const handleCloseClick = () => {
    const params: Record<string, string> = {
      app_id: appId ?? JUSID_PLATFORM_WEB_AUTH,
      jusid_platform: JUSID_PLATFORM_WEB_AUTH,
      type: 'LogInModal',
    }
    if (featureCode) params.feature_code = featureCode
    if (fromComponent) params.from_component = fromComponent
    sendMetrics(MODAL_EVENTS.CLOSED, null, params)
    window.location.href = PRODUCTION_ORIGIN
  }

  return (
    <AuthLayout footerShowAgreementText>
      <EmailSignIn
        initialEmail={initialEmail}
        isLoading={isLoading}
        error={error}
        onSubmit={handleEmailSubmit}
        appId={appId}
        featureCode={featureCode}
        fromComponent={fromComponent}
        nextUrl={nextUrl}
        scope={scope}
        onCloseClick={handleCloseClick}
      />
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps = async (ctx) => {
  // Warms the browser's UA Client Hints cache for www.jusbrasil.com.br
  // so the POST /bifrost/api/login that follows this page render carries
  // Sec-CH-UA-* headers, which the bifrost device-fingerprint composer
  // reads for arch_detected, mobile_model_name, browser_version, and
  // operating_system_version (UA-CH primary, UA parsing fallback).
  ctx.res.setHeader(
    'Accept-CH',
    'Sec-CH-UA-Arch, Sec-CH-UA-Bitness, Sec-CH-UA-Mobile, Sec-CH-UA-Model, Sec-CH-UA-Platform, Sec-CH-UA-Platform-Version, Sec-CH-UA-Full-Version-List'
  )

  const { client, withApolloProps } = withApollo(ctx)
  const referer = ctx.req.headers.referer ?? null
  const [{ csrfToken }, signupModal] = await Promise.all([
    internalAuthApi.getCsrfToken(ctx),
    resolveSignupModalVariant({ ctx, apolloClient: client, referer }),
  ])
  const pathname = ctx.resolvedUrl?.split('?')[0] ?? ''
  const basePath = getBasePath(pathname)
  const signupModalEntityName =
    typeof ctx.query.signupModalEntityName === 'string' ? ctx.query.signupModalEntityName : null
  const rawType = ctx.query.signupModalEntityType
  const signupModalEntityType = rawType === 'PF' || rawType === 'CNPJ' ? rawType : null
  const entityNoticeParams = resolveEntityNoticeParams(ctx.query)
  // /login is a direct campaign-email landing (no signup query string yet) —
  // see campaign-attribution.ts for the utm_campaign/cookie fallback.
  const { appId, featureCode, fromComponent } = withCampaignFallback(
    getEventDataFromQuery(ctx.query),
    ctx.query,
    ctx
  )
  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      ...withApolloProps(),
      ...signupModal.featureFlagsProps,
      csrfToken,
      basePath,
      ...(appId && { appId }),
      ...(featureCode && { featureCode }),
      ...(fromComponent && { fromComponent }),
      signupModalForcedVariant: signupModal.forcedVariant,
      signupModalSegment: signupModal.segment,
      signupModalEntityName,
      signupModalEntityType,
      signupModalExposureEligible: suppressSignupModalExposure(
        signupModal.eligible,
        entityNoticeParams
      ),
      signupModalInExperiment: signupModal.inExperiment,
      ...(entityNoticeParams && {
        entityNoticeVariant: entityNoticeParams.variant,
        entityNoticeEntityId: entityNoticeParams.entityId,
        entityNoticePageKind: entityNoticeParams.pageKind,
      }),
    },
  }
}

export const LoginPageExports = {
  getServerSideProps,
}
